import {planeArrival} from '@img/index.js'

/**
 * THE WAY BACK INTO THE ARRIVAL GUIDE — a plane, bottom right, gently pulsing.
 *
 * ── ⚠️ IT IS THE ONLY WAY IN, not a convenience ──────────────────────────────────
 * `/:tripId/welcome` was deleted (2026-09-23), so there is no URL for the guide any more.
 * Everything else about it is automatic and fires ONCE: the modal opens itself when the
 * flight lands, and the trigger holds a per-trip guard precisely so a 2s poll cannot reopen
 * it over a passenger who closed it — which means nothing reopens it FOR them either.
 * Without this button, one stray tap on the backdrop loses the booth, the walk and the
 * driver card until a reload.
 *
 * ── ⚠️ IT ONLY EXISTS WHILE IT MEANS SOMETHING ───────────────────────────────────
 * A from-airport ride on the ongoing list, from two hours before its pickup until the ride
 * starts. Earlier than that a plane floats over every page for hours and becomes furniture;
 * once the ride has started the passenger is in the car and the guide is about a walk they
 * have finished. It is NOT gated on the flight being down — "where do I go when I land" is
 * asked before the aircraft arrives, and the guide answers it then too (`FlightOnTheWay`).
 * The caller owns all of that (`guideAvailable` and `useArrivalGuideWindow` in `AppLayout`),
 * so this component stays a button.
 *
 * ── ⚠️ IT BLINKS THE WAY THE NOTIFICATION DOT BLINKS ─────────────────────────────
 * `animate-ping` on a copy of the shape, expanding out of it and fading — exactly
 * `NotifyDot`, the green dot on the header's bell. The portal already had one idea of
 * "something here wants you", and a second one (this was an `animate-pulse` halo, which
 * breathes in place rather than radiating) reads as a different kind of signal for no
 * reason. Reusing the bell's means a passenger recognises it without being taught.
 *
 * ⚠️ AND IT IS THE SAME BLACK AS THE BUTTON, not a green ring. The green halo sat OVER the
 * dark circle at 60% and drained it to a murky sage, so the "black button" was never black
 * on screen. The ring is `--bg-dark` — #201e20, the button's own colour — so what radiates
 * is the button itself.
 *
 * ⚠️ IT PINGS THE RING, NOT THE ICON. Animating the button makes the plane throb, which at
 * 56px reads as a rendering fault rather than an invitation.
 */
export default function ArrivalGuideButton({onClick}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="Open your arrival guide"
            /* ⚠️ ABOVE THE PAGE, BELOW THE MODAL. `Modal` sits at z-99999; this has to
               clear ordinary page furniture and nothing else, or it would float over the
               guide it opens. */
            className="fixed bottom-6 right-6 z-9990 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-(--bg-dark) shadow-[0_6px_20px_rgba(0,0,0,0.25)] transition-opacity hover:opacity-85 max-md:bottom-4 max-md:right-4 max-md:h-12 max-md:w-12"
        >
            {/* The ping. `pointer-events-none` so it can never eat the tap it is
                advertising, and `aria-hidden` because it says nothing a screen reader
                needs; the button's own label does that.

                ⚠️ INSET TO 75%, WHICH IS WHAT HALVES THE SPREAD. `animate-ping` always
                scales its element to 2x, so a ring starting at the button's full size threw
                a black wave 56px past the edge — far too loud for a corner. Starting it at
                three quarters lands the same 2x at 1.5x of the button, i.e. half the
                overshoot, while keeping Tailwind's own timing curve rather than forking the
                keyframe. The part that starts UNDER the button is never seen: it is the same
                black, over the same black. */}
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-[12.5%] animate-ping rounded-full bg-(--bg-dark) opacity-75"
            />
            {/* ⚠️ `plane-arrival`, WHICH IS THE LANDING TWIN OF `plane-take-off`. They are a
                pair from the same set — one flat `#201E20` path each, the same wide layout,
                one climbing and one coming down — so this is the take-off icon's landing
                counterpart rather than a lookalike from another family.

                `brightness-0 invert` paints it flat white over the black circle; `relative`
                lifts it over the ping. */}
            <img
                src={planeArrival}
                alt=""
                className="relative h-6 w-6 brightness-0 invert max-md:h-5 max-md:w-5"
            />
        </button>
    )
}
